/**
 * Converts the internal message history format to the format expected by the OpenAI API.
 * Ensures that assistant messages with tool_calls are correctly followed by tool responses.
 * 
 * @param {Array} messages - The internal array of message objects.
 * @param {string|null} [includeUserMessage=null] - An optional current user message to append.
 * @returns {Array} - The formatted array of messages for the API.
 */
import { logger } from '../utils/logger';
export const convertMessagesToApiFormat = (messages, includeUserMessage = null) => {
    const formattedMessages = messages.map(msg => {
        const isAssistant = msg.author === 'ai';
        const isUser = msg.author === 'user';
        const isTool = msg.author === 'tool'; // Check for tool author

        // Base structure determines role
        const apiMsg = {
            role: isAssistant ? 'assistant' : isUser ? 'user' : isTool ? 'tool' : 'system',
            // Content is required, use text, fallback to empty string if needed.
            // Will be adjusted below for specific roles (e.g., assistant with tool_calls).
            content: msg.text || ""
        };

        // Handle Assistant messages
        if (isAssistant) {
            if (msg.tool_calls && msg.tool_calls.length > 0) {
                apiMsg.tool_calls = msg.tool_calls;
                // API requires content to be null or string. If tool_calls are present,
                // content is optional. Use original text if present, otherwise null.
                apiMsg.content = msg.text || null;
            } else {
                // Ensure content is a string for regular assistant messages
                 apiMsg.content = msg.text || "";
            }
        }
        // Handle Tool messages
        else if (isTool) {
            if (msg.tool_call_id) {
                apiMsg.tool_call_id = msg.tool_call_id;
                apiMsg.name = msg.name || 'unknown_tool_name'; // API requires name
                // API requires content for tool role. Use original msg.content if available,
                // otherwise fallback to msg.text, ensuring it's a string.
                apiMsg.content = typeof msg.content === 'string' ? msg.content : (msg.text || '');
                // Ensure content is stringified if it was originally an object
                 if (typeof msg.content !== 'string' && msg.content != null) {
                    try {
                        apiMsg.content = JSON.stringify(msg.content);
                    } catch (e) {
                        logger.error("Failed to stringify tool content:", msg.content, e);
                        apiMsg.content = "[Error stringifying tool content]";
                    }
                 } else if (msg.content == null) {
                     apiMsg.content = msg.text || ""; // Fallback to text if content is null/undefined
                 }

            } else {
                logger.warn("Tool message found without tool_call_id:", msg);
                return null; // Skip invalid tool messages that lack an ID
            }
        }
        // Handle User and System messages (ensure content is string)
        else {
             apiMsg.content = msg.text || "";
        }

        return apiMsg;

    }).filter(msg => msg !== null); // Filter out any skipped messages (like invalid tool messages)

    // Add current user message if provided
    if (includeUserMessage) {
        formattedMessages.push({ role: 'user', content: includeUserMessage });
    }

    // Final validation/cleanup: Ensure tool messages have string content
    formattedMessages.forEach(msg => {
        if (msg.role === 'tool' && typeof msg.content !== 'string') {
            logger.warn("Tool message content is not a string after mapping, converting:", msg);
            try {
                msg.content = JSON.stringify(msg.content);
            } catch (e) {
                 logger.error("Failed to stringify tool content during final validation:", msg.content, e);
                 msg.content = "[Error stringifying tool content]";
            }
        }
        // Ensure assistant message with tool_calls has null content if text was originally empty/null
        if (msg.role === 'assistant' && msg.tool_calls && msg.tool_calls.length > 0 && msg.content === "") {
             // If the original msg.text was empty/null, content should be null
             // This is handled correctly by `apiMsg.content = msg.text || null;` above
        }
    });

    return formattedMessages;
};